import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { useRorosDomain } from '@/hooks/roros/domains';
import { useRorosActors } from '@/hooks/roros/actors';
import { ActorAvatar } from '@/components/roros/ActorAvatar';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { t } from '@/lib/i18n';
import { RorosActorType } from '@/types/roros/enums';
// R-4 item 7 (J0-F Wave F.4): Was a duplicated local function (also in ActorDetailPage).
// Extracted to shared lib; local copy removed.
import { actorTypeLabel } from '@/lib/roros/actorLabels';

// W2-G: Static switch helper replaces dotted-key t() calls.
function adoptionLevelLabel(level: string | null | undefined): string {
  switch (level) {
    case 'shadow': return t('Shadow');
    case 'copilot': return t('Copilot');
    case 'autopilot': return t('Autopilot');
    default: return level ?? '';
  }
}

export function DomainDetailPage() {
  const { id } = useParams<{ id: string }>();

  const domainQuery = useRorosDomain(id ?? '');
  const actorsQuery = useRorosActors(id ? { domainId: id } : undefined);

  return (
    <RorosPage
      title={t('Domain Details')}
      breadcrumbs={[{ label: t('Domains'), path: '/roros/domains' }]}
    >
      <QueryPage query={domainQuery} emptyMessage={t('Domain not found')}>
        {(domain) => (
          <div className="space-y-6 mt-6">
            {/* Header */}
            <div className="flex items-center gap-3">
              <Link to="/roros/domains">
                <Button size="sm" variant="outline">
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                  {t('Back')}
                </Button>
              </Link>
              <h2 className="text-xl font-semibold">{domain.name}</h2>
              {domain.settings?.isAdvisory && (
                <Badge variant="secondary">{t('Advisory')}</Badge>
              )}
              <Badge variant={domain.isActive ? 'success' : 'secondary'}>
                {domain.isActive ? t('Active') : t('Inactive')}
              </Badge>
            </div>

            {/* R-4 item 8 (J0-F): Activity tab removed — no implementation yet; "coming soon" stub
                without a feature flag adds dead UI. Re-add under VITE_SHOW_ACTIVITY_TAB when
                the activity feed ships. */}
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">{t('Overview')}</TabsTrigger>
                <TabsTrigger value="members">{t('Members')}</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="space-y-4 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('Domain Information')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {domain.description && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">{t('Description')}</div>
                          <p className="text-sm">{domain.description}</p>
                        </div>
                      )}
                      {domain.settings?.defaultAdoptionLevel && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">
                            {t('Default Adoption Level')}
                          </div>
                          <Badge variant="outline">
                            {adoptionLevelLabel(domain.settings.defaultAdoptionLevel)}
                          </Badge>
                        </div>
                      )}
                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <span>
                          {domain.actorCount} {domain.actorCount !== 1 ? t('actors') : t('actor')}
                        </span>
                        <span>
                          {domain.workItemCount}{' '}
                          {domain.workItemCount !== 1 ? t('work items') : t('work item')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="members">
                <div className="space-y-4 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('Members')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {actorsQuery.isLoading ? (
                        <p className="text-sm text-muted-foreground">{t('Loading...')}</p>
                      ) : actorsQuery.data && actorsQuery.data.length > 0 ? (
                        <div className="space-y-2">
                          {actorsQuery.data.map((actor) => (
                            <div
                              key={actor.id}
                              className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                            >
                              <ActorAvatar actor={actor} size="sm" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{actor.name}</div>
                              </div>
                              <Badge
                                className={
                                  actor.actorType === RorosActorType.HUMAN
                                    ? 'bg-blue-100 text-blue-700'
                                    : actor.actorType === RorosActorType.AI_OPERATOR
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-amber-100 text-amber-700'
                                }
                              >
                                {actorTypeLabel(actor.actorType)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('No actors assigned to this domain.')}</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

            </Tabs>
          </div>
        )}
      </QueryPage>
    </RorosPage>
  );
}
